import { FLOW_HEX, FlowColor, FlowDefs, SvgFlow, SvgNode } from './parts';

/**
 * A2A / MCP Gateway 아키텍처.
 * 좌측의 여러 Agent/Client 가 중앙 Agentgateway 허브를 통해
 * 우측의 MCP Server / API / 외부 Agent 와 표준 프로토콜로 통신한다.
 *
 * 게이트웨이가 각 기능을 "어디서" 수행하는지 아키텍처 내 해당 지점에 배지로 표시하고,
 * 전체 기능 목록은 카드 하단에 함께 리스트업한다.
 */

const FEATURES = [
  { t: '프로토콜 지원', d: 'MCP · A2A' },
  { t: '인증·인가', d: 'JWT · OAuth · RBAC' },
  { t: '관측성', d: '트레이싱·메트릭·로그' },
  { t: '도구 페더레이션', d: '멀티플렉싱' },
  { t: '트래픽 관리', d: '라우팅·레이트리밋' },
  { t: '멀티테넌시', d: '동적 설정 리로드' },
];

/** 아키텍처 내부의 기능 수행 지점 배지 */
function FnTag({ cx, cy, label, color }: { cx: number; cy: number; label: string; color: FlowColor }) {
  const w = label.length * 10.5 + 22;
  const h = 24;
  return (
    <g className="ai-fn-tag">
      <rect x={cx - w / 2} y={cy - h / 2} width={w} height={h} rx={12} stroke={FLOW_HEX[color]} />
      <text x={cx} y={cy + 1} textAnchor="middle" dominantBaseline="middle" fontSize={11}>
        {label}
      </text>
    </g>
  );
}

export default function GatewayDiagram() {
  const id = 'gw';
  return (
    <div className="ai-diagram">
      <svg viewBox="0 0 820 440" role="img" aria-label="A2A · MCP Gateway 아키텍처">
        <FlowDefs idPrefix={id} />

        {/* 좌 → 허브 */}
        <SvgFlow idPrefix={id} color="cyan" d="M180,92 C 250,92 255,180 350,180" />
        <SvgFlow idPrefix={id} color="cyan" d="M180,222 C 255,222 270,222 350,222" delay={0.4} />
        <SvgFlow idPrefix={id} color="cyan" d="M180,352 C 250,352 255,264 350,264" delay={0.8} />

        {/* 허브 → 우 */}
        <SvgFlow idPrefix={id} color="purple" d="M520,180 C 600,180 585,92 650,92" delay={0.2} />
        <SvgFlow idPrefix={id} color="purple" d="M520,222 C 595,222 580,222 650,222" delay={0.6} />
        <SvgFlow idPrefix={id} color="purple" d="M520,264 C 600,264 585,352 650,352" delay={1} />

        {/* 좌측: 에이전트 / 클라이언트 */}
        <SvgNode x={20} y={60} title="AI Agent" sub="A2A Client" variant="cyan" delay={0} />
        <SvgNode x={20} y={190} title="LLM App" sub="MCP Host" variant="cyan" delay={0.6} />
        <SvgNode x={20} y={320} title="Client" sub="외부 시스템" variant="cyan" delay={1.2} />

        {/* 중앙 허브 */}
        <SvgNode
          x={350}
          y={162}
          w={170}
          h={120}
          title="Agentgateway"
          sub="A2A · MCP Gateway"
          variant="core"
          titleSize={17}
          rx={16}
        />

        {/* 우측: 도구 / 서버 / 외부 에이전트 */}
        <SvgNode x={650} y={60} title="MCP Server" sub="Tools" variant="purple" delay={0.3} />
        <SvgNode x={650} y={190} title="REST API" sub="Backend" variant="purple" delay={0.9} />
        <SvgNode x={650} y={320} title="Agent (A2A)" sub="External" variant="purple" delay={1.5} />

        {/* 기능 수행 지점 배지 */}
        <FnTag cx={300} cy={120} label="인증·인가" color="cyan" />
        <FnTag cx={437} cy={138} label="관측성" color="cyan" />
        <FnTag cx={437} cy={306} label="프로토콜 변환 · 멀티플렉싱" color="accent" />
        <FnTag cx={580} cy={132} label="라우팅 · 레이트리밋" color="purple" />
      </svg>

      <div className="ai-diagram__sub">Agentgateway 주요 기능</div>
      <div className="ai-diagram__features">
        {FEATURES.map((f) => (
          <div className="ai-feature" key={f.t}>
            <span className="ai-feature__dot" />
            <div>
              <b className="ai-feature__title">{f.t}</b>
              <span className="ai-feature__desc">{f.d}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
